import { useEffect, useRef, useState } from 'react';
import { FormatSample, IssueList, RejectedFile, SnModal } from './common';
import { countInRange, rangeWarnings } from './parse';
import { readNotesFile } from './readNotesFile';
import { downloadTemplate } from './template';
import { countPdfPages } from './pdfPageCount';
import './speakerNotes.css';

const MAX_MANUAL_PAGES = 1000;

const parseManualCount = (value) => {
    const number = Number(String(value).trim());
    return Number.isInteger(number) && number >= 1 && number <= MAX_MANUAL_PAGES ? number : null;
};

const previewLines = (text) => {
    const lines = text.split('\n').filter((line) => line.trim());
    return { head: lines.slice(0, 2).map((line) => (line.length > 140 ? `${line.slice(0, 140)}…` : line)), rest: Math.max(0, lines.length - 2) };
};

// 1-qadam: "Spiker matnlari (ixtiyoriy)". Generatsiya formasi ichida turadi, shuning uchun
// barcha tugmalar type="button" — aks holda forma yuborilib, generate-speech-text chaqiriladi.
export default function SpeakerNotesBlock({ pdfFile, pdfName, value, onChange }) {
    const inputRef = useRef(null);
    const [rejected, setRejected] = useState(null);
    const [pdfCount, setPdfCount] = useState({ status: 'idle', count: null });
    const [manualCount, setManualCount] = useState('');
    const [showSample, setShowSample] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    // PDF almashsa sahifalar soni qaytadan aniqlanadi
    useEffect(() => {
        setPdfCount({ status: 'idle', count: null });
    }, [pdfFile]);

    const ensurePdfCount = async () => {
        if (!pdfFile) return null;
        if (pdfCount.status === 'ok') return pdfCount.count;
        setPdfCount({ status: 'loading', count: null });
        try {
            const count = await countPdfPages(pdfFile);
            setPdfCount({ status: 'ok', count });
            return count;
        } catch (error) {
            setPdfCount({ status: 'failed', count: null });
            return null;
        }
    };

    // TXT yuklangan bo'lsa, N/M uchun PDF sahifalar soni kerak
    useEffect(() => {
        if (value && pdfFile && pdfCount.status === 'idle') ensurePdfCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, pdfFile, pdfCount.status]);

    const handleTxtChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        const result = await readNotesFile(file);
        if (result.errors.length > 0) {
            setRejected(result);
            return;
        }
        setRejected(null);
        onChange(result);
    };

    const manual = parseManualCount(manualCount);
    const pageCount = pdfCount.status === 'ok' ? pdfCount.count : manual;
    const needsManual = !pdfFile || pdfCount.status === 'failed';

    const handleTemplate = async () => {
        const count = needsManual ? manual : await ensurePdfCount();
        if (count) downloadTemplate(count, pdfName);
    };

    const found = value ? countInRange(value, pageCount) : 0;
    const warnings = value ? [...value.warnings, ...rangeWarnings(value, pageCount)] : [];
    const pages = value ? Object.keys(value.pages).map(Number).sort((a, b) => a - b) : [];

    return (
        <div className="sn-block">
            <div className="sn-block__head">
                <span className="sn-block__title">Spiker matnlari (ixtiyoriy)</span>
                <span className="sn-block__hint">
                    Har sahifa matni alohida qatordagi <code>=== N ===</code> bilan boshlanadi.{' '}
                    <button type="button" className="sn-link" onClick={() => setShowSample((open) => !open)} aria-expanded={showSample}>
                        {showSample ? 'Namunani yashirish' : 'Format namunasi'}
                    </button>
                </span>
            </div>
            {showSample && <FormatSample />}

            <div className="sn-actions">
                <button type="button" className="sn-btn sn-btn--primary" onClick={() => inputRef.current?.click()}>
                    TXT yuklash
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".txt,text/plain"
                    className="sn-hidden"
                    data-testid="sn-txt-input"
                    onChange={handleTxtChange}
                />

                {needsManual ? (
                    <span className="sn-manual">
                        <label className="sn-manual__label">
                            Sahifalar soni
                            <input
                                type="text"
                                inputMode="numeric"
                                className="sn-manual__input"
                                value={manualCount}
                                onChange={(event) => setManualCount(event.target.value.replace(/[^\d]/g, ''))}
                                onKeyDown={(event) => {
                                    // Enter formani yubormasin (generate-speech-text)
                                    if (event.key === 'Enter') event.preventDefault();
                                }}
                            />
                        </label>
                        <button type="button" className="sn-btn" onClick={handleTemplate} disabled={!manual}>
                            Shablonni yuklab olish
                        </button>
                    </span>
                ) : (
                    <button type="button" className="sn-btn" onClick={handleTemplate} disabled={pdfCount.status === 'loading'}>
                        {pdfCount.status === 'loading' ? 'PDF o\'qilmoqda…' : 'Shablonni yuklab olish'}
                    </button>
                )}
            </div>
            {needsManual && (
                <div className="sn-note">
                    {pdfFile
                        ? "PDF sahifalar sonini aniqlab bo'lmadi (shifrlangan yoki buzilgan bo'lishi mumkin) — sonini qo'lda kiriting."
                        : 'PDF hali tanlanmagan — shablon uchun sahifalar sonini kiriting.'}
                </div>
            )}

            <RejectedFile result={rejected} />

            {value && (
                <div className="sn-summary">
                    <div className="sn-summary__row">
                        <span className="sn-summary__file" title={value.fileName}>{value.fileName}</span>
                        <span className="sn-summary__count">
                            {pageCount ? `${found}/${pageCount} sahifaga matn topildi` : `${found} ta sahifaga matn topildi`}
                        </span>
                        <span className="sn-summary__actions">
                            <button type="button" className="sn-btn sn-btn--small" onClick={() => setPreviewOpen(true)}>Ko'rish</button>
                            <button
                                type="button"
                                className="sn-btn sn-btn--small sn-btn--danger"
                                onClick={() => { onChange(null); setPreviewOpen(false); }}
                            >
                                Olib tashlash
                            </button>
                        </span>
                    </div>
                    {warnings.length > 0 && (
                        <div className="sn-alert sn-alert--warning">
                            <IssueList items={warnings} />
                        </div>
                    )}
                </div>
            )}

            {previewOpen && value && (
                <SnModal title={`Fayldagi matnlar — ${value.fileName}`} onClose={() => setPreviewOpen(false)}>
                    <ol className="sn-preview">
                        {pages.map((page) => {
                            const { head, rest } = previewLines(value.pages[page]);
                            const outside = page < 1 || (pageCount && page > pageCount);
                            return (
                                <li key={page} className={outside ? 'sn-preview__item sn-preview__item--outside' : 'sn-preview__item'}>
                                    <div className="sn-preview__page">
                                        {page}-sahifa {outside && <span className="sn-badge sn-badge--neutral">PDF'da yo'q</span>}
                                    </div>
                                    {head.map((line, index) => <div key={index} className="sn-preview__line">{line}</div>)}
                                    {rest > 0 && <div className="sn-preview__more">… yana {rest} qator</div>}
                                </li>
                            );
                        })}
                    </ol>
                </SnModal>
            )}
        </div>
    );
}
